// FRAUDNET integration — PayPal FraudNet device-data collection, ported from the
// legacy multi-page `fraudNetIntegration`. An integration rather than page
// runtime so it can be switched on per pipe / per distribution and A/B tested.
//
// Flow: once the page has loaded and the persisted variables are merged, reuse
// the session id (the FraudNet CMID, PayPal's `f`) stored on
// `var.global.trackingProperties.fraudNetSessionId` or mint one, write it back,
// then append the JSON parameter block fb.js looks for and the fb.js loader.
// `usePaymentData` reads the field into the payments-API `fraudNetSessionId`;
// PayPal expects that id back as the `PAYPAL-CLIENT-METADATA-ID` header on the
// payment call.
//
// Invariants:
//
//   * The id in the payment must be the `f` fb.js reported, or PayPal cannot
//     join the transaction to the device data. So the id is never rotated once
//     the script has run: `pipe:data-cleared` writes the same id back rather
//     than minting one, and TRACKING_PROPERTIES carries the field over when it
//     re-anchors the snapshot.
//   * The id is resolved only after `whenReady()`. Before the persisted merge
//     the stored id is invisible, and anything written then is overwritten.
//   * Always write the WHOLE trackingProperties object: `setData` is a flat-map
//     write, so a dotted key creates a sibling variable that shadows reads.
//   * PayPal caps both `f` and `s` at 32 characters.
//
// fb.js reads the parameter block once, so this collects once per document
// load with the landing page as `s` — in an SPA one document spans the whole
// funnel. Re-collecting per page would mean rewriting the block and calling
// `window.PAYPAL.asyncData.initAndCollect()`, which fb.js exposes but PayPal
// does not document.

import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type {
  TRACKING_PROPERTIES_VAR_KEY as TrackingPropertiesVarKey,
  TrackingProperties,
} from '../trackingProperties/trackingProperties'
import type { PipeDisplayMode, PipeInterface } from '@render/composables/usePipe'

/**
 * Spelled out rather than value-imported from TRACKING_PROPERTIES, which may be
 * off on this pipe; the annotation pins it to the owner's constant.
 */
const TRACKING_PROPERTIES_VAR_KEY: typeof TrackingPropertiesVarKey = 'var.global.trackingProperties'

/** Fixed by PayPal: fb.js finds its parameter block by this attribute value. */
export const FRAUD_NET_FNCLS = 'fnparams-dede7cc5-15fd-4c75-a9f4-36c430ee3a99'

export const FRAUD_NET_SCRIPT_URL = 'https://c.paypal.com/da/r/fb.js'

/** PayPal's limit for both the session id (`f`) and the source id (`s`). */
export const FRAUD_NET_MAX_ID_LENGTH = 32

const SESSION_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'
const SESSION_ID_PATTERN = /^[A-Za-z0-9]{1,32}$/

const STATIC_MODE = 'static' as PipeDisplayMode.STATIC

export type FraudNetPipe = Pick<PipeInterface, 'getData' | 'setData'>

/* -------------------------------------------------------------------------- */
/* Pure rules                                                                  */
/* -------------------------------------------------------------------------- */

/** A fresh 32-character `[a-z0-9]` id — the legacy shape. */
export function generateSessionId(): string {
  const bytes = new Uint8Array(FRAUD_NET_MAX_ID_LENGTH)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => SESSION_ID_ALPHABET.charAt(b % SESSION_ID_ALPHABET.length)).join(
    '',
  )
}

export function isUsableSessionId(value: unknown): value is string {
  return typeof value === 'string' && SESSION_ID_PATTERN.test(value)
}

/** Last non-empty path segment — legacy `window.getPage()`. */
export function pageSlugFromPath(pathname: string): string {
  return pathname.split('/').filter(Boolean).pop() ?? ''
}

/** PayPal's `s`: `<brandCode>_<page slug>`, as legacy built it, within the 32-char cap. */
export function buildSourceId(brandCode: unknown, pathname: string): string {
  const brand = typeof brandCode === 'string' ? brandCode : ''
  return [brand, pageSlugFromPath(pathname)]
    .filter(Boolean)
    .join('_')
    .slice(0, FRAUD_NET_MAX_ID_LENGTH)
}

/* -------------------------------------------------------------------------- */
/* DOM + pipe wrappers                                                         */
/* -------------------------------------------------------------------------- */

const readTrackingProperties = (pipe: FraudNetPipe): Partial<TrackingProperties> => {
  const raw = pipe.getData(TRACKING_PROPERTIES_VAR_KEY)
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Partial<TrackingProperties>)
    : {}
}

/** The id already stored on trackingProperties, if it is one fb.js could take. */
export function readSessionId(pipe: FraudNetPipe): string | undefined {
  const stored = readTrackingProperties(pipe).fraudNetSessionId
  return isUsableSessionId(stored) ? stored : undefined
}

/**
 * Put the id on trackingProperties. Writes only on a difference: every
 * committing setData re-serializes the whole variable map into localStorage.
 */
export function writeSessionId(pipe: FraudNetPipe, sessionId: string): void {
  const tp = readTrackingProperties(pipe)
  if (tp.fraudNetSessionId === sessionId) return
  pipe.setData(TRACKING_PROPERTIES_VAR_KEY, { ...tp, fraudNetSessionId: sessionId })
}

/** Append the parameter block and the fb.js loader, once per document. */
export function injectFraudNet(sessionId: string, sourceId: string): void {
  if (document.querySelector(`script[fncls="${FRAUD_NET_FNCLS}"]`)) return

  const params = document.createElement('script')
  params.type = 'application/json'
  params.setAttribute('fncls', FRAUD_NET_FNCLS)
  params.text = JSON.stringify({ f: sessionId, s: sourceId })
  document.body.appendChild(params)

  const loader = document.createElement('script')
  loader.src = FRAUD_NET_SCRIPT_URL
  loader.async = true
  document.body.appendChild(loader)
}

const pageLoaded = (): Promise<void> =>
  document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }))

/* -------------------------------------------------------------------------- */
/* Integration                                                                 */
/* -------------------------------------------------------------------------- */

const fraudNetIntegration: PipeIntegration = (): PipeIntegrationDef => {
  let sessionId: string | undefined

  return {
    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return
      // The editor preview would report authors' devices under ids no payment carries.
      if (pipe.currentMode.value !== STATIC_MODE) return

      // After `load`, so fb.js and the iframes it opens stay off the first paint.
      void pageLoaded()
        .then(() => pipe.whenReady())
        .then(() => {
          const id = readSessionId(pipe) ?? generateSessionId()
          sessionId = id
          writeSessionId(pipe, id)
          injectFraudNet(
            id,
            buildSourceId(pipe.getData('const.system.brandCode'), window.location.pathname),
          )
        })

      // clearPersistedData wiped the variable; fb.js has already reported this id.
      pipe.subscribe('pipe:data-cleared', () => {
        if (sessionId) writeSessionId(pipe, sessionId)
      })
    },
  }
}

export default fraudNetIntegration
